import { useEffect, useState, type FormEvent } from 'react';
import { Button, Dialog, Select, TextField, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useCreateRole, type MatrixRole } from './api';
import styles from './PermissionsPage.module.css';

interface CreateRoleDialogProps {
  open: boolean;
  roles: MatrixRole[];
  onClose: () => void;
}

/**
 * „Rolle anlegen" (Admin 01, own dialog): name and „Vorlage" (an existing role whose template is copied) →
 * `POST /roles`; the new role appears as a column of the matrix.
 */
export function CreateRoleDialog({ open, roles, onClose }: CreateRoleDialogProps) {
  const { toast } = useToast();
  const create = useCreateRole();
  const [name, setName] = useState('');
  const [basedOn, setBasedOn] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setError(null);
    setBasedOn(roles.find((role) => role.key === 'advisor')?.id ?? roles[0]?.id ?? '');
  }, [open, roles]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim() === '') {
      setError('Bitte geben Sie einen Namen an.');
      return;
    }
    setError(null);
    try {
      const role = await create.mutateAsync({ name: name.trim(), basedOnRoleId: basedOn || undefined });
      toast({ message: `Rolle „${role.name}“ angelegt.` });
      onClose();
    } catch (failure) {
      setError(
        failure instanceof ApiError ? (failure.fields['name'] ?? failure.message) : errorMessage(failure),
      );
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Rolle anlegen"
      title="Neue Rolle"
      subtitle="Die Rolle startet mit den Rechten der Vorlage. Sie passen sie danach in der Matrix an."
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form="create-role-form" loading={create.isPending}>
            Rolle anlegen
          </Button>
        </>
      }
    >
      <form id="create-role-form" className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <TextField
          label="Name"
          required
          value={name}
          error={error ?? undefined}
          onChange={(event) => setName(event.target.value)}
        />
        <Select
          label="Vorlage"
          value={basedOn}
          options={roles.map((role) => ({ value: role.id, label: role.name }))}
          onChange={(event) => setBasedOn(event.target.value)}
        />
      </form>
    </Dialog>
  );
}
